import React, { useReducer, useEffect, forwardRef, useCallback } from 'react';
import { Empty, Spin } from 'antd';
import cn from 'classnames';
import { TreeCheckProvider } from './context';
import {
  treeCheckReducer,
  treeCheckStateHelpers,
  treeCheckActionTypes as actionTypes,
} from './state';
import TreeCheckNodeList from './ui/TreeCheckNodeList/TreeCheckNodeList';
import { treeCheckThemes } from './constants/treeCheckThemes';
import { updateCheckedKeys } from './utils/updateCheckedKeys';
import styles from './index.module.scss';

const { createTreeCheckInitState } = treeCheckStateHelpers;

const TreeCheck = forwardRef((props, ref) => {
  const {
    treeData,
    loadData,
    onExpand,
    onCheck,
    getSearch,
    checkedKeys: incomingCheckedKeys,
    halfCheckedKeys: incomingHalfCheckedKeys,
    loading = false,
    theme,
    rootClassName,
    checkable = true,
  } = props;

  const [state, dispatch] = useReducer(
    treeCheckReducer,
    createTreeCheckInitState({
      checkedKeys: incomingCheckedKeys,
      halfCheckedKeys: incomingHalfCheckedKeys,
    })
  );

  const handleNodeCheck = useCallback(
    (_e, node, checked) => {
      const {
        checkedKeys: oriCheckedKeys,
        halfCheckedKeys: oriHalfCheckedKeys,
      } = state;

      const { checkedKeys, halfCheckedKeys } = updateCheckedKeys({
        checkedKeys: oriCheckedKeys,
        halfCheckedKeys: oriHalfCheckedKeys,
        node,
        items: treeData,
        checked,
      });

      if (typeof onCheck === 'function') {
        onCheck(Array.from(checkedKeys));
      }

      dispatch({
        type: actionTypes.setCheckedAndHalfCheckedKeys,
        payload: {
          checkedKeys,
          halfCheckedKeys,
        },
      });
    },
    [
      JSON.stringify(Array.from(state.checkedKeys)),
      JSON.stringify(Array.from(state.halfCheckedKeys)),
      JSON.stringify(treeData),
    ]
  );

  useEffect(() => {
    const incoming = JSON.stringify(incomingCheckedKeys);
    const current = JSON.stringify(Array.from(state.checkedKeys));

    if (incoming !== current) {
      dispatch({
        type: actionTypes.setCheckedKeys,
        payload: incomingCheckedKeys,
      });
    }
  }, [incomingCheckedKeys]);

  useEffect(() => {
    const incoming = JSON.stringify(incomingHalfCheckedKeys);
    const current = JSON.stringify(Array.from(state.halfCheckedKeys));

    if (incoming !== current) {
      dispatch({
        type: actionTypes.setHalfCheckedKeys,
        payload: incomingHalfCheckedKeys,
      });
    }
  }, [incomingHalfCheckedKeys]);

  return (
    <TreeCheckProvider
      state={state}
      dispatch={dispatch}
    >
      <Spin spinning={loading}>
        {(treeData || []).length ? (
          <TreeCheckNodeList
            treeData={treeData}
            loadData={loadData}
            onExpand={onExpand}
            onCheck={handleNodeCheck}
            getSearch={getSearch}
            theme={theme}
            className={cn(styles.root, rootClassName)}
            checkable={checkable}
            itemsToRender={treeData.filter((i) => !i.parentId)}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Spin>
    </TreeCheckProvider>
  );
});

export { treeCheckThemes };

export default TreeCheck;
